import React, { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageHeader, Container } from "@/components/PageShell";
import { StatusBadge, RoleBadge } from "@/components/Badges";
import { Plus, Play, CheckCircle2, Edit2, Trash2, RefreshCw, Loader2, X, ChevronDown, ChevronRight, RotateCcw, ArrowUpCircle } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
    { value: "standalone", label: "Standalone — прямой выход" },
    { value: "ru_relay", label: "RU Relay — российский ретранслятор" },
    { value: "foreign_exit", label: "Foreign Exit — зарубежный выход" },
];

function ServerForm({ initial, onClose, onSaved }) {
    const [data, setData] = useState(initial || {
        name: "", host: "", ssh_port: 22, ssh_user: "root",
        ssh_password: "", ssh_private_key: "",
        role: "standalone", domain: "", country: "",
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setErr("");
        try {
            const payload = {
                ...data,
                ssh_port: parseInt(data.ssh_port) || 22,
                ssh_password: data.ssh_password || null,
                ssh_private_key: data.ssh_private_key || null,
                domain: data.domain || null,
                country: data.country || null,
            };
            if (initial) await api.put(`/servers/${initial.id}`, payload);
            else await api.post("/servers", payload);
            toast.success(initial ? "Сервер обновлён" : `Сервер добавлен — создано 3 профиля (Package B)`);
            onSaved();
        } catch (e) { setErr(extractError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-zinc-950/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-white border border-zinc-200 rounded-md shadow-xl w-full max-w-xl max-h-[90vh] overflow-auto"
                data-testid="server-form-modal"
            >
                <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                    <h2 className="text-base font-semibold">{initial ? "Редактировать сервер" : "Добавить сервер"}</h2>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Название">
                            <input required data-testid="server-name-input" className={inputCls} value={data.name} onChange={(e) => set("name", e.target.value)} placeholder="Amsterdam VPS" />
                        </Field>
                        <Field label="Роль">
                            <select data-testid="server-role-select" className={inputCls} value={data.role} onChange={(e) => set("role", e.target.value)}>
                                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                        </Field>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                            <Field label="SSH Host (IP или домен)">
                                <input required data-testid="server-host-input" className={`${inputCls} font-mono`} value={data.host} onChange={(e) => set("host", e.target.value)} placeholder="203.0.113.10" />
                            </Field>
                        </div>
                        <Field label="SSH Port">
                            <input type="number" data-testid="server-ssh-port-input" className={`${inputCls} font-mono`} value={data.ssh_port} onChange={(e) => set("ssh_port", e.target.value)} />
                        </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="SSH User">
                            <input data-testid="server-ssh-user-input" className={`${inputCls} font-mono`} value={data.ssh_user} onChange={(e) => set("ssh_user", e.target.value)} />
                        </Field>
                        <Field label="Домен (для TLS-профилей)">
                            <input data-testid="server-domain-input" className={`${inputCls} font-mono`} value={data.domain || ""} onChange={(e) => set("domain", e.target.value)} placeholder="vpn.example.com" />
                        </Field>
                    </div>

                    <Field label="Страна сервера (ISO-2 — управляет авто-подбором SNI)">
                        <select
                            data-testid="server-country-select"
                            className={inputCls}
                            value={data.country || ""}
                            onChange={(e) => set("country", e.target.value)}
                        >
                            <option value="">— Глобальный (Apple/MS/Cloudflare)</option>
                            <option value="RU">🇷🇺 RU (yandex, mail.ru, gosuslugi…)</option>
                            <option value="FI">🇫🇮 FI (yle, nokia, supercell…)</option>
                            <option value="DE">🇩🇪 DE (tagesschau, sap, lufthansa…)</option>
                            <option value="NL">🇳🇱 NL (rabobank, ing, kpn…)</option>
                            <option value="PL">🇵🇱 PL (allegro, onet, wp.pl, mbank…)</option>
                            <option value="US">🇺🇸 US (apple, microsoft, github…)</option>
                            <option value="GB">🇬🇧 GB (bbc, guardian, barclays…)</option>
                            <option value="FR">🇫🇷 FR (lemonde, orange, bnp…)</option>
                            <option value="SE">🇸🇪 SE (svt, spotify, ikea…)</option>
                        </select>
                    </Field>

                    {data.role === "ru_relay" && (
                        <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs rounded-md px-3 py-2">
                            💡 Домен указывайте здесь (на RU Relay) — RU IP доступен из российских сетей без VPN, LE-сертификат выпускается без проблем, а в Фазе B на этом же сервере будет decoy-сайт + скрытый /admin.
                        </div>
                    )}
                    {data.role === "foreign_exit" && (
                        <div className="bg-zinc-50 border border-zinc-200 text-zinc-700 text-xs rounded-md px-3 py-2">
                            ℹ️ Для foreign exit домен обычно не нужен — активным будет только Reality-профиль (он используется как внутренний туннель от RU Relay). TLS-профили будут автоматически пропущены при деплое.
                        </div>
                    )}

                    <Field label={`SSH Password ${initial?.has_password ? "(оставьте пустым — не менять)" : "(необяз. если есть ключ)"}`}>
                        <input type="password" data-testid="server-ssh-password-input" className={`${inputCls} font-mono`} value={data.ssh_password || ""} onChange={(e) => set("ssh_password", e.target.value)} placeholder={initial?.has_password ? "Сохранён — не менять" : ""}/>
                    </Field>

                    <Field label={`SSH Private Key ${initial?.has_private_key ? "(оставьте пустым — не менять)" : "(PEM, необязательно)"}`}>
                        <textarea rows={4} data-testid="server-ssh-key-input" className={`${inputCls} font-mono text-xs`} value={data.ssh_private_key || ""} onChange={(e) => set("ssh_private_key", e.target.value)} placeholder={initial?.has_private_key ? "Сохранён — не менять" : "-----BEGIN OPENSSH PRIVATE KEY-----"} />
                    </Field>

                    {!initial && (
                        <div className="bg-orange-50 border border-orange-200 text-orange-900 text-xs rounded-md px-3 py-2.5 space-y-1">
                            <div className="font-semibold">Пакет профилей по умолчанию (Package B):</div>
                            <div className="font-mono text-[11px] space-y-0.5">
                                <div>• Primary: VLESS Reality + TCP :443 (без домена)</div>
                                <div>• Mobile-safe: VLESS + TLS + WebSocket :8443 (требует домен)</div>
                                <div>• Stealth: Trojan + TLS + gRPC :2053 (требует домен)</div>
                            </div>
                            <div className="pt-1">
                                <b>Без домена</b> работает только профиль 1. Домен можно указать позже — и пересинкать.
                            </div>
                        </div>
                    )}

                    {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{err}</div>}
                </div>

                <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50">
                    <button type="button" onClick={onClose} className={btnSecondary} data-testid="server-form-cancel">Отмена</button>
                    <button type="submit" disabled={saving} className={btnPrimary} data-testid="server-form-submit">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {initial ? "Сохранить" : "Добавить"}
                    </button>
                </div>
            </form>
        </div>
    );
}

const inputCls = "w-full px-3 py-2 bg-white border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#FA3E05] focus:border-[#FA3E05]";
const btnPrimary = "bg-[#FA3E05] hover:bg-[#E03504] disabled:opacity-60 text-white text-sm font-medium rounded-md px-4 py-2 transition-colors inline-flex items-center gap-2";
const btnSecondary = "bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-900 text-sm font-medium rounded-md px-4 py-2 transition-colors inline-flex items-center gap-2";
const btnGhost = "hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 text-sm rounded-md px-2.5 py-1.5 transition-colors inline-flex items-center gap-1.5";

function Field({ label, children }) {
    return (
        <div className="space-y-1">
            <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">{label}</label>
            {children}
        </div>
    );
}

export default function Servers() {
    const [servers, setServers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [expanded, setExpanded] = useState({});

    const load = async () => {
        try {
            const { data } = await api.get("/servers");
            setServers(data);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const testSsh = async (id) => {
        setBusyId(id + ":test");
        try {
            const { data } = await api.post(`/servers/${id}/test`);
            if (data.success) toast.success("SSH соединение установлено");
            else toast.error(data.error);
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusyId(null); load(); }
    };

    const deploy = async (id) => {
        setBusyId(id + ":deploy");
        try {
            const { data } = await api.post(`/servers/${id}/deploy`);
            toast.success("Деплой запущен — следите в журнале");
            window.location.href = `/deployments?id=${data.deployment_id}`;
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusyId(null); load(); }
    };

    const sync = async (id) => {
        setBusyId(id + ":sync");
        try {
            const { data } = await api.post(`/servers/${id}/sync`);
            toast.success("Конфиг синхронизирован");
            window.location.href = `/deployments?id=${data.deployment_id}`;
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusyId(null); }
    };

    const upgradeXray = async (id) => {
        if (!window.confirm("Обновить Xray до последней стабильной версии? Конфиг сохранится, но сервис перезапустится — будет короткий разрыв соединения. Перед обновлением убедитесь, что конфиг совместим с новой версией.")) return;
        setBusyId(id + ":upgrade");
        try {
            const { data } = await api.post(`/servers/${id}/upgrade-xray`);
            toast.success("Запущено обновление Xray");
            window.location.href = `/deployments?id=${data.deployment_id}`;
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusyId(null); }
    };

    const del = async (id) => {
        if (!window.confirm("Удалить сервер? Связанные маршруты будут удалены, пользователи отвязаны.")) return;
        try {
            await api.delete(`/servers/${id}`);
            toast.success("Сервер удалён");
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const toggleProfile = async (serverId, profileId, active) => {
        try {
            await api.patch(`/servers/${serverId}/profiles/${profileId}?active=${active}`);
            toast.success(active ? "Профиль активирован" : "Профиль деактивирован");
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const resetProfiles = async (serverId) => {
        if (!window.confirm("Пересоздать профили из шаблона Package B? ВСЕ старые клиентские конфиги перестанут работать.")) return;
        try {
            await api.post(`/servers/${serverId}/profiles/reset`);
            toast.success("Профили пересозданы. Не забудьте выдать пользователям новые конфиги.");
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const updateProfilePort = async (serverId, profileId, newPort) => {
        try {
            await api.put(`/servers/${serverId}/profiles/${profileId}`, { port: parseInt(newPort) });
            toast.success("Порт обновлён — не забудьте нажать Sync/Deploy");
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const updateProfileSni = async (serverId, profileId, newSni) => {
        try {
            await api.put(`/servers/${serverId}/profiles/${profileId}`, { reality_sni: newSni });
            toast.success("SNI обновлён — авто-синк на этом сервере запущен");
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const updateProfileFingerprint = async (serverId, profileId, fp) => {
        try {
            await api.put(`/servers/${serverId}/profiles/${profileId}`, { fingerprint: fp });
            toast.success(`Fingerprint: ${fp} — авто-синк запущен`);
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const updateProfileSniMode = async (serverId, profileId, mode) => {
        try {
            await api.put(`/servers/${serverId}/profiles/${profileId}`, { sni_mode: mode });
            toast.success(`SNI режим: ${mode} — авто-синк запущен`);
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const protoLabel = (p) => {
        const m = {
            "vless-reality": "Reality",
            "vless-tls": "VLESS+TLS",
            "trojan": "Trojan",
        };
        return m[p] || p;
    };

    return (
        <>
            <PageHeader
                title="Серверы"
                subtitle="Управление узлами инфраструктуры"
                testid="servers-header"
                actions={
                    <button onClick={() => setModal("new")} className={btnPrimary} data-testid="add-server-btn">
                        <Plus className="w-4 h-4" /> Добавить сервер
                    </button>
                }
            />
            <Container>
                <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="servers-table">
                    {loading ? (
                        <div className="p-10 text-center text-sm text-zinc-500">Загрузка…</div>
                    ) : servers.length === 0 ? (
                        <div className="p-14 text-center">
                            <div className="text-sm text-zinc-700 font-medium">Серверов ещё нет</div>
                            <div className="text-xs text-zinc-500 mt-1">Добавьте первый узел — система автоматически развернёт 3 транспортных профиля одновременно</div>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-zinc-50 border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-500">
                                    <th className="px-4 py-3 font-medium w-8"></th>
                                    <th className="px-4 py-3 font-medium">Название</th>
                                    <th className="px-4 py-3 font-medium">Host</th>
                                    <th className="px-4 py-3 font-medium">Роль</th>
                                    <th className="px-4 py-3 font-medium">Профили</th>
                                    <th className="px-4 py-3 font-medium">Статус</th>
                                    <th className="px-4 py-3 font-medium text-right">Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {servers.map((s) => {
                                    const isOpen = expanded[s.id];
                                    const activeCount = s.profiles?.filter(p => p.active).length || 0;
                                    return (
                                    <React.Fragment key={s.id}>
                                    <tr className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`server-row-${s.id}`}>
                                        <td className="px-2 py-3">
                                            <button onClick={() => setExpanded({ ...expanded, [s.id]: !isOpen })} className="p-1 hover:bg-zinc-100 rounded" data-testid={`expand-${s.id}`}>
                                                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 font-medium whitespace-nowrap">
                                            {s.name}
                                            {s.domain && <div className="text-[10px] font-mono text-zinc-500">{s.domain}</div>}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-zinc-700 whitespace-nowrap">{s.host}:{s.ssh_port}</td>
                                        <td className="px-4 py-3"><RoleBadge role={s.role} /></td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center gap-1 text-xs font-mono text-zinc-700">
                                                <span className="text-[#FA3E05] font-semibold">{activeCount}</span>
                                                <span className="text-zinc-400">/ {s.profiles?.length || 0} активных</span>
                                            </span>
                                        </td>
                                        <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                                                <button onClick={() => testSsh(s.id)} disabled={busyId === s.id + ":test"} className={btnGhost} data-testid={`test-ssh-${s.id}`}>
                                                    {busyId === s.id + ":test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                                    Тест
                                                </button>
                                                <button onClick={() => deploy(s.id)} disabled={busyId === s.id + ":deploy"} className={btnGhost} data-testid={`deploy-${s.id}`}>
                                                    {busyId === s.id + ":deploy" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                                                    Деплой
                                                </button>
                                                <button onClick={() => sync(s.id)} disabled={busyId === s.id + ":sync"} className={btnGhost} data-testid={`sync-${s.id}`}>
                                                    <RefreshCw className="w-3.5 h-3.5" /> Sync
                                                </button>
                                                <button onClick={() => upgradeXray(s.id)} disabled={busyId === s.id + ":upgrade"} className={btnGhost} data-testid={`upgrade-xray-${s.id}`} title="Обновить Xray до последней стабильной версии">
                                                    {busyId === s.id + ":upgrade" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpCircle className="w-3.5 h-3.5" />}
                                                    Update
                                                </button>
                                                <button onClick={() => setModal(s)} className={btnGhost} data-testid={`edit-${s.id}`}>
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button onClick={() => del(s.id)} className={`${btnGhost} hover:!text-red-600`} data-testid={`delete-${s.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="bg-zinc-50/50 border-b border-zinc-200">
                                            <td colSpan={7} className="px-6 py-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Профили</div>
                                                    <button onClick={() => resetProfiles(s.id)} className="text-xs text-zinc-600 hover:text-[#FA3E05] inline-flex items-center gap-1" data-testid={`reset-profiles-${s.id}`}>
                                                        <RotateCcw className="w-3 h-3" /> Пересоздать профили
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                                    {(s.profiles || []).map(p => {
                                                        // Block ACTIVATION when prerequisites are missing (no domain for TLS),
                                                        // but ALWAYS allow DEACTIVATION — otherwise users get stuck with a
                                                        // misconfigured profile they can't disable.
                                                        const disabled = !p.active && p.needs_tls && !p.domain;
                                                        return (
                                                        <div key={p.id} className={`border rounded-md p-3 ${p.active ? "bg-white border-zinc-200" : "bg-zinc-100 border-zinc-200 opacity-60"}`} data-testid={`profile-${p.id}`}>
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    <div className="text-sm font-semibold truncate">{p.name}</div>
                                                                    <div className="text-[11px] font-mono text-zinc-600 mt-0.5 flex items-center gap-1">
                                                                        <span>{protoLabel(p.protocol)} · {p.transport} · :</span>
                                                                        <input
                                                                            type="number"
                                                                            defaultValue={p.port}
                                                                            onBlur={(e) => {
                                                                                const v = parseInt(e.target.value);
                                                                                if (v && v !== p.port) updateProfilePort(s.id, p.id, v);
                                                                            }}
                                                                            data-testid={`port-input-${p.id}`}
                                                                            className="w-14 px-1 py-0 border border-zinc-200 rounded text-[11px] font-mono bg-white hover:border-[#FA3E05] focus:outline-none focus:border-[#FA3E05]"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <label className="relative inline-flex items-center cursor-pointer shrink-0" title={disabled ? "Нужен домен" : (p.active ? "Деактивировать" : "Активировать")}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={p.active}
                                                                        disabled={disabled}
                                                                        onChange={(e) => toggleProfile(s.id, p.id, e.target.checked)}
                                                                        data-testid={`toggle-profile-${p.id}`}
                                                                        className="sr-only peer"
                                                                    />
                                                                    <div className="w-9 h-5 bg-zinc-300 peer-checked:bg-[#FA3E05] rounded-full peer-focus:ring-2 peer-focus:ring-[#FA3E05]/30 peer-disabled:opacity-50 relative transition-colors">
                                                                        <div className={`absolute top-0.5 left-0.5 bg-white w-4 h-4 rounded-full transition-transform ${p.active ? "translate-x-4" : ""}`}></div>
                                                                    </div>
                                                                </label>
                                                            </div>
                                                            {p.needs_tls && (
                                                                <div className="text-[10px] text-zinc-500 mt-2 font-mono truncate">
                                                                    {p.domain ? `domain: ${p.domain}` : <span className="text-amber-600">требуется домен</span>}
                                                                </div>
                                                            )}
                                                            {p.protocol === "vless-reality" && (
                                                                <div className="space-y-1.5 mt-2">
                                                                    <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-1">
                                                                        <span>SNI режим:</span>
                                                                        <select
                                                                            value={p.sni_mode || "custom"}
                                                                            onChange={(e) => updateProfileSniMode(s.id, p.id, e.target.value)}
                                                                            className="px-1 py-0.5 border border-zinc-300 rounded text-[10px] font-mono bg-white focus:border-[#FA3E05] focus:outline-none"
                                                                            data-testid={`sni-mode-${p.id}`}
                                                                        >
                                                                            <option value="custom">✍️ Custom (1 SNI)</option>
                                                                            <option value="list">📋 List</option>
                                                                            <option value="auto">🎲 Auto ({s.country || "Global"})</option>
                                                                        </select>
                                                                        {(p.sni_mode === "auto" || p.sni_mode === "list") && p.reality_sni_list && (
                                                                            <span className="text-[10px] text-zinc-400 ml-1">
                                                                                ({p.reality_sni_list.length} SNI)
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-1">
                                                                        <span>SNI:</span>
                                                                        <input
                                                                            type="text"
                                                                            defaultValue={p.reality_sni}
                                                                            onBlur={(e) => {
                                                                                const v = e.target.value.trim();
                                                                                if (v && v !== p.reality_sni) updateProfileSni(s.id, p.id, v);
                                                                            }}
                                                                            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                                                            disabled={p.sni_mode === "auto"}
                                                                            className="px-1 py-0.5 border border-zinc-300 rounded text-[10px] font-mono w-[160px] focus:border-[#FA3E05] focus:outline-none disabled:bg-zinc-100 disabled:text-zinc-400"
                                                                            data-testid={`sni-input-${p.id}`}
                                                                            title={p.sni_mode === "auto" ? "В Auto-режиме SNI выбирается из списка страны автоматически" : "Reality SNI — фейковый домен под который маскируемся"}
                                                                        />
                                                                    </div>
                                                                    <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-1">
                                                                        <span>Fingerprint:</span>
                                                                        <select
                                                                            value={p.fingerprint || "chrome"}
                                                                            onChange={(e) => updateProfileFingerprint(s.id, p.id, e.target.value)}
                                                                            className="px-1 py-0.5 border border-zinc-300 rounded text-[10px] font-mono bg-white focus:border-[#FA3E05] focus:outline-none"
                                                                            data-testid={`fingerprint-${p.id}`}
                                                                            title="TLS browser fingerprint — клиент будет выглядеть как этот браузер на handshake"
                                                                        >
                                                                            <option value="chrome">chrome</option>
                                                                            <option value="firefox">firefox</option>
                                                                            <option value="safari">safari</option>
                                                                            <option value="ios">ios</option>
                                                                            <option value="android">android</option>
                                                                            <option value="edge">edge</option>
                                                                            <option value="random">random (рандом каждый коннект)</option>
                                                                            <option value="randomized">randomized</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {(p.protocol === "vless-tls" || p.protocol === "trojan") && (
                                                                <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-1 mt-2">
                                                                    <span>Fingerprint:</span>
                                                                    <select
                                                                        value={p.fingerprint || "chrome"}
                                                                        onChange={(e) => updateProfileFingerprint(s.id, p.id, e.target.value)}
                                                                        className="px-1 py-0.5 border border-zinc-300 rounded text-[10px] font-mono bg-white focus:border-[#FA3E05] focus:outline-none"
                                                                        data-testid={`fingerprint-${p.id}`}
                                                                    >
                                                                        <option value="chrome">chrome</option>
                                                                        <option value="firefox">firefox</option>
                                                                        <option value="safari">safari</option>
                                                                        <option value="ios">ios</option>
                                                                        <option value="android">android</option>
                                                                        <option value="edge">edge</option>
                                                                        <option value="random">random</option>
                                                                    </select>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );})}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                );})}
                            </tbody>
                        </table>
                    )}
                </div>

                {servers.length > 0 && (
                    <div className="mt-6 text-xs text-zinc-500 font-mono">
                        Конфигурация: 3 транспортных профиля работают одновременно
                    </div>
                )}
            </Container>

            {modal && (
                <ServerForm
                    initial={modal === "new" ? null : modal}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); load(); }}
                />
            )}
        </>
    );
}
